import React, { useEffect, useState } from "react";
import { Dialog, Divider, Skeleton, CircularProgress } from "@mui/material";
import { approveIcon, arrowIcon, rejectIcon } from "./svg";
import api from "../api/api";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { eventBus } from "../utils/eventBus";
import { getPracticeId } from "../config/practice";

const NotificationsModal = ({ open, onClose, fetchCount, threadNotificationCount }) => {
  const navigate = useNavigate();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [activeSection, setActiveSection] = useState("pto"); // "pto" or "threads"
  const [threadNotifications, setThreadNotifications] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadReadLoading, setThreadReadLoading] = useState({});
  const practiseid = getPracticeId();
  const user = useSelector((state) => state.userconfig);
  console.log("user",user);
  
  const handleToggle = (index) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };
   const homescreenprev = useSelector(
        (state) => state.userPrivileges.homescreen
      );
      console.log(homescreenprev,'home')

  // Fetch PTO data
  const fetchData = async () => {
    try {
      setLoading(true);
      const startDate = dayjs().subtract(1, "month").format("YYYY-MM-DD");
      const endDate = dayjs().add(1, "month").format("YYYY-MM-DD");

      const response = await api.get(`/v1/${practiseid}/pto/requests`, {
        params: { start_date: startDate, end_date: endDate, status: "Pending" },
      });

      if (response.status === 200 && response.data) {
        setNotifications(response.data || []);
        fetchCount();
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Thread Notifications
  const fetchThreadNotifications = async () => {
    try {
      setThreadLoading(true);
      const response = await api.get(
        `/v1/${practiseid}/nonvisitnotes?recipientid=${user?.id}`
      );
      if (response.status === 200) {
        setThreadNotifications(response.data || []);
      } else {
        setThreadNotifications([]);
      }
    } catch (error) {
      console.error("Error fetching thread notifications:", error);
      setThreadNotifications([]);
    } finally {
      setThreadLoading(false);
    }
  };

  // Mark thread as read
  const handleMarkAsRead = async (noteId) => {
    try {
      setThreadReadLoading((prev) => ({ ...prev, [noteId]: true }));
      const response = await api.put(
        `/v1/${practiseid}/nonvisitnote/${noteId}/read`
      );
      if (response.status === 200) {
        setThreadNotifications((prev) =>
          prev.filter((item) => item.id !== noteId)
        );
        fetchCount();
      }
    } catch (error) {
      console.error("Error marking thread as read:", error);
    } finally {
      setThreadReadLoading((prev) => ({ ...prev, [noteId]: false }));
    }
  };

  // Navigate to the patient's thread
  const handleThreadClick = async (thread) => {
    await handleMarkAsRead(thread.id);
    onClose();
    navigate("/landing");
    setTimeout(() => {
      eventBus.emit("openPatientThread", { patientid: thread.patientid });
    }, 200);
  };

  // Mark all threads as read
  const handleMarkAllThreadsAsRead = async () => {
    const unreadThreads = threadNotifications.filter((t) => t.is_read === 0);
    for (const thread of unreadThreads) {
      await handleMarkAsRead(thread.id);
    }
  };

  // Approve/Reject handler
  const handleStatusUpdate = async (id, status) => {
    try {
      setButtonLoading((prev) => ({ ...prev, [id]: true }));

      const response = await api.put(`/v1/${practiseid}/pto/request/${id}`, {
        status: status,
        approved_by: user?.id || null,
      });

      if (response.status === 200) {
        setNotifications((prev) => prev.filter((item) => item.id !== id));
        fetchCount();
      } else {
        console.warn("Update failed:", response?.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error updating PTO status:", error);
    } finally {
      setButtonLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const hasNotificationPrivilege = homescreenprev?.includes("Notifications");

  useEffect(() => {
    if (open && hasNotificationPrivilege) {
      if (homescreenprev?.includes("Request Time Off")) {
        fetchData();
      }
      if (user?.id) {
        fetchThreadNotifications();
      }
    }
  }, [open, homescreenprev]);

  const displayedNotifications = showAll
    ? notifications
    : notifications.slice(0, 5);

  const unreadThreads = threadNotifications.filter((t) => t.is_read === 0);
  const displayedThreads = showAll ? unreadThreads : unreadThreads.slice(0, 5);

  if (!hasNotificationPrivilege) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-3 pb-2">
        <h2 className="text-xl text-[#1E2939] font-inter font-medium">
          Notifications
        </h2>
        <button
          onClick={() => {
            if (activeSection === "threads") {
              handleMarkAllThreadsAsRead();
            }
          }}
          className="text-[#155DFC] font-medium font-inter text-sm focus:outline-none"
        >
          Mark all as read
        </button>
      </div>

      {/* Tabs */}
      <div className="flex px-6 gap-4 pb-2">
        <button
          onClick={() => { setActiveSection("pto"); setExpandedIndex(null); setShowAll(false); }}
          className={`text-sm font-inter font-medium pb-1 border-b-2 transition-all ${
            activeSection === "pto"
              ? "text-[#1D2951] border-[#1D2951]"
              : "text-gray-400 border-transparent"
          }`}
        >
          PTO Requests
          {notifications.length > 0 && (
            <span className="ml-1 text-[10px] bg-[#E17954] text-white rounded-full px-1.5 py-0.5">
              {notifications.length}
            </span>
          )}
        </button>
        <button
          onClick={() => { setActiveSection("threads"); setExpandedIndex(null); setShowAll(false); }}
          className={`text-sm font-inter font-medium pb-1 border-b-2 transition-all ${
            activeSection === "threads"
              ? "text-[#1D2951] border-[#1D2951]"
              : "text-gray-400 border-transparent"
          }`}
        >
          Threads
          {unreadThreads.length > 0 && (
            <span className="ml-1 text-[10px] bg-[#E17954] text-white rounded-full px-1.5 py-0.5">
              {unreadThreads.length}
            </span>
          )}
        </button>
      </div>

      <Divider />

      {/* Scrollable area */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* PTO Section */}
        {activeSection === "pto" && (
          <>
            {loading ? (
              <div className="px-6 py-3 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="rectangular" height={60} />
                  </div>
                ))}
              </div>
            ) : displayedNotifications.length > 0 ? (
              displayedNotifications.map((notification, index) => (
                <div key={notification.id || index}>
                  <button
                    onClick={() => handleToggle(index)}
                    className="w-full flex justify-between items-center px-6 py-3 text-left hover:bg-gray-50 focus:outline-none"
                  >
                    <span className="text-sm text-[#1E2939] font-inter font-normal">
                      PTO Request from {notification.emp_name}
                    </span>
                    <span
                      className={`text-gray-400 text-lg transform transition-transform duration-300 ${
                        expandedIndex === index ? "rotate-90" : "rotate-0"
                      }`}
                    >
                      {arrowIcon}
                    </span>
                  </button>

                  {expandedIndex === index && (
                    <div className="px-6 py-4">
                      <div className="flex justify-center flex-col bg-white rounded-xl border border-gray-100 shadow-sm px-2 py-2">
                        <div className="flex justify-between items-start">
                          <div className="flex flex-col">
                            <div className="text-xs font-inter leading-[20px] font-medium text-[#000000]">
                              {notification.emp_name}
                            </div>
                            <div className="text-[10px] leading-4 font-normal font-inter text-[#6B7280]">
                              {notification.emp_role}
                            </div>
                            <div className="text-[10px] font-normal font-inter text-[#4B5563] mt-2">
                              {dayjs(notification.start_date).format("MMM D")} –{" "}
                              {dayjs(notification.end_date).format("MMM D")}{" "}
                              <span className="text-gray-400">
                                ({notification.total_hours} hrs)
                              </span>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-3">
                            <span className="bg-[#DBEAFE] text-[#1E40AF] text-[10px] font-inter font-normal pl-2 pr-3 py-2 rounded-full leading-none">
                              {notification.request_type}
                            </span>
                          </div>
                        </div>
                        {/* Approve / Reject Buttons */}
                        <div className="flex gap-2 justify-end mt-2">
                          <button
                            disabled={buttonLoading[notification.id]}
                            onClick={() =>
                              handleStatusUpdate(notification.id, "Approved")
                            }
                            className={`w-6 h-6 rounded-md flex items-center justify-center transition focus:outline-none ${
                              buttonLoading[notification.id]
                                ? "bg-gray-100 opacity-60 cursor-not-allowed"
                                : "bg-[#DCFCE7] hover:bg-[#BBF7D0]"
                            }`}
                            aria-label="Approve"
                          >
                            {approveIcon}
                          </button>

                          <button
                            disabled={buttonLoading[notification.id]}
                            onClick={() =>
                              handleStatusUpdate(notification.id, "Denied")
                            }
                            className={`w-6 h-6 rounded-md flex items-center justify-center transition focus:outline-none ${
                              buttonLoading[notification.id]
                                ? "bg-gray-100 opacity-60 cursor-not-allowed"
                                : "bg-[#FEE2E2] hover:bg-[#FCA5A5]"
                            }`}
                            aria-label="Reject"
                          >
                            {rejectIcon}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <Divider />
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500 text-sm">
                No pending notifications
              </div>
            )}
          </>
        )}

        {/* Threads Section */}
        {activeSection === "threads" && (
          <>
            {threadLoading ? (
              <div className="px-6 py-3 space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="rectangular" height={40} />
                  </div>
                ))}
              </div>
            ) : displayedThreads.length > 0 ? (
              displayedThreads.map((thread, index) => (
                <div key={thread.id || index}>
                  <div
                    className="w-full flex justify-between items-center px-6 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleThreadClick(thread)}
                  >
                    <div className="flex flex-col flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#E17954] flex-shrink-0"></div>
                        <span className="text-sm text-[#1E2939] font-inter font-medium">
                          New thread from {thread.firstname} {thread.lastname}
                        </span>
                      </div>
                      {thread.subject && (
                        <span className="text-xs text-gray-500 font-inter ml-4 mt-0.5">
                          Subject: {thread.subject}
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400 font-inter ml-4 mt-0.5">
                        {thread.activity_text
                          ? thread.activity_text.length > 80
                            ? thread.activity_text.substring(0, 80) + "..."
                            : thread.activity_text
                          : ""}
                      </span>
                      <span className="text-[10px] text-gray-400 font-inter ml-4 mt-0.5">
                        {dayjs(thread.created_at).format("MMM D, YYYY h:mm A")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {thread.category && (
                        <span className="bg-[#DBEAFE] text-[#1E40AF] text-[10px] font-inter font-normal px-2 py-1 rounded-full leading-none">
                          {thread.category}
                        </span>
                      )}
                      {threadReadLoading[thread.id] ? (
                        <CircularProgress size={16} />
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(thread.id);
                          }}
                          className="text-[10px] text-[#155DFC] font-inter hover:underline focus:outline-none"
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                  <Divider />
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500 text-sm">
                No unread thread notifications
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {((activeSection === "pto" && notifications.length > 5) ||
        (activeSection === "threads" && unreadThreads.length > 5)) && (
        <div className="px-6 py-4 border-t border-gray-200 flex justify-center">
          <button
            onClick={() => setShowAll((prev) => !prev)}
            className="text-center text-[#155DFC] focus:outline-none font-normal font-inter text-sm"
          >
            {showAll ? "Show less" : "View all"}
          </button>
        </div>
      )}
    </Dialog>
  );
};

export default NotificationsModal;
